import { Sparkles, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { generatePalette, TREATMENTS } from '@/lib/helpPalette'
import { HELP_SURFACE_KEYS } from '@/pages/HelpCenter/types'
import type { HelpCenterTheme } from '@/pages/HelpCenter/types'

interface PaletteStudioProps {
  theme: HelpCenterTheme
  // Brand primary/accent inherited from Product & Brand — seed the generator
  // and the swatch fallbacks so a new help center starts on-brand.
  brandPrimary?: string | null
  brandAccent?: string | null
  onThemeChange: (patch: Partial<HelpCenterTheme>) => void
}

type ColorKey = keyof HelpCenterTheme

// Fallbacks mirror the base sets in help_center.css so the swatch shows the
// real default before an override is set. page falls back to the outer canvas.
const DEFAULTS: Record<'light' | 'dark', Record<string, string>> = {
  dark: {
    bg_color: '#0b0f14', bg_accent_color: '#0f151c', page_color: '#0b0f14',
    surface_color: '#141a22', surface_hover_color: '#19212b', border_color: '#232c38',
    text_color: '#e7ebf0', muted_color: '#93a1b2',
  },
  light: {
    bg_color: '#f6f8fb', bg_accent_color: '#eef1f6', page_color: '#f6f8fb',
    surface_color: '#ffffff', surface_hover_color: '#fbfcfe', border_color: '#e3e8ef',
    text_color: '#1a2330', muted_color: '#5b6776',
  },
}

const SURFACE_FIELDS: { key: ColorKey; label: string }[] = [
  { key: 'bg_color', label: 'Outer canvas' },
  { key: 'page_color', label: 'Page band' },
  { key: 'surface_color', label: 'Cards' },
  { key: 'surface_hover_color', label: 'Card hover' },
  { key: 'border_color', label: 'Borders' },
  { key: 'text_color', label: 'Text' },
  { key: 'muted_color', label: 'Muted text' },
]

function ColorField({ label, value, fallback, onChange }: {
  label: string; value?: string; fallback: string; onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || fallback}
          onChange={e => onChange(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded border border-border"
        />
        <Input value={value ?? ''} onChange={e => onChange(e.target.value)} className="font-mono" placeholder={fallback} />
      </div>
    </div>
  )
}

export function PaletteStudio({ theme, brandPrimary, brandAccent, onThemeChange }: PaletteStudioProps) {
  const [treatmentIdx, setTreatmentIdx] = useState(0)
  const seed = theme.primary_color || brandPrimary || '#10b981'
  // Accent tints the hover/border layer; inherits the brand accent, then the
  // seed, when not explicitly set on this help center.
  const accent = theme.accent_color || brandAccent || seed
  const defaults = DEFAULTS[theme.appearance === 'light' ? 'light' : 'dark']
  const hasCustom = HELP_SURFACE_KEYS.some(k => !!theme[k])

  // One button: each click generates the next treatment, cycling through them.
  const generate = () => {
    onThemeChange(generatePalette(seed, accent, TREATMENTS[treatmentIdx].key))
    setTreatmentIdx((treatmentIdx + 1) % TREATMENTS.length)
  }

  const clearCustom = () => {
    const cleared = Object.fromEntries(HELP_SURFACE_KEYS.map(k => [k, undefined])) as Partial<HelpCenterTheme>
    onThemeChange(cleared)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 p-3">
        <div className="mr-auto text-sm text-muted-foreground">
          Generate a cohesive palette from your brand colour — click again for another variation. Fine-tune any layer below.
        </div>
        <Button variant="outline" size="sm" onClick={generate}>
          <Sparkles className="size-4" /> Generate palette
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ColorField label="Brand colour (primary)" value={theme.primary_color} fallback={brandPrimary || '#10b981'} onChange={v => onThemeChange({ primary_color: v })} />
        <ColorField label="Accent" value={theme.accent_color} fallback={brandAccent || theme.primary_color || brandPrimary || '#10b981'} onChange={v => onThemeChange({ accent_color: v })} />
      </div>

      <div className="space-y-3 border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <Label className="text-muted-foreground">Backgrounds &amp; surfaces</Label>
          {hasCustom && (
            <Button variant="ghost" size="sm" onClick={clearCustom}>
              <RotateCcw className="size-3.5" /> Reset to default
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {SURFACE_FIELDS.map(({ key, label }) => (
            <ColorField
              key={key}
              label={label}
              value={theme[key] as string | undefined}
              fallback={defaults[key] ?? '#000000'}
              onChange={v => onThemeChange({ [key]: v })}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
